/**
 * The one regex that recognises a browser transport failure, and a predicate
 * for the case where all you have left is a message STRING. Issue #1709.
 *
 * ── Why this module exists ────────────────────────────────────────────────
 *
 * "Load failed" (WebKit), "Failed to fetch" (Chrome) and "Network request
 * failed" (RN/webview) all mean the same thing: the request never completed.
 * Lost signal, app backgrounded, phone asleep. Nothing failed in the app and
 * nothing a mom did is recoverable by telling her about it.
 *
 * This wording lived in `authNetworkError.ts` and was reachable only through
 * `isAuthNetworkError`, which is the RIGHT shape for GoTrue and the WRONG
 * shape everywhere else — see below. #1709 needed the same wording on a path
 * that has no error object at all, and a second copy of the regex is how the
 * two quietly drift apart. `authNetworkError.ts` now imports it from here, so
 * there is exactly one.
 *
 * ── Three shapes, and only one of them has a `name` ───────────────────────
 *
 * The same dropped connection arrives in three different shapes depending on
 * which layer caught it, which is the whole reason a single detector cannot
 * cover them:
 *
 *   1. **GoTrue / auth** — `AuthRetryableFetchError`, `status: 0`. Has a
 *      `name`. `isAuthNetworkError` handles it and keeps doing so.
 *   2. **PostgREST / DB writes** — a plain object, `status: 0`, and
 *      **`error.name` is `undefined`** (the type name is embedded in the
 *      message string instead). A `name`-keyed check returns false on every
 *      one of these, which is why `createConversationWithRetry` tests
 *      `status === 0` and deliberately does NOT match on message text. See
 *      `createConversationWithRetry.ts:28-47` — measured against a real
 *      client, not inferred. **Do not route a PostgREST failure through this
 *      module**; it has a status field that answers the question exactly.
 *   3. **A caught-and-flattened throw** — what `requestChatTitle` returns:
 *      `{ ok: false, failure: "threw", detail: "Load failed" }`. By the time
 *      it reaches a caller there is no error object left, just prose. That is
 *      the case `isTransportFailureMessage` exists for, and the ONLY case.
 *
 * So: shape 1 → `isAuthNetworkError`. Shape 2 → `status === 0`. Shape 3 →
 * here. Picking by convenience rather than by shape is how this fails
 * silently, because the wrong detector returns a confident `false`.
 */

/** Safari ("Load failed"), Chrome ("Failed to fetch"), RN/webview variants. */
export const TRANSPORT_MESSAGE = /load failed|failed to fetch|network ?request failed/i;

/**
 * True when a message string carries a transport-failure signature.
 *
 * Takes `unknown` on purpose: every caller is holding an optional `detail`
 * off a result object, and a non-string (undefined, an object, a number) is a
 * legitimate "no, and don't throw about it" rather than a programming error.
 *
 * ⚠️ Message matching is a LAST resort and is browser- and locale-dependent.
 * If the value you are holding still has a `status`, use that instead.
 */
export function isTransportFailureMessage(text: unknown): boolean {
  return typeof text === "string" && TRANSPORT_MESSAGE.test(text);
}
